import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { units, Link } from '@hbf/dsl';
import { LightBulb2Light, ArrowButtonRightBold } from '@ha/icons';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { useTrackEvent } from 'ha/modules/Analytics/helpers/TrackEvent';
import { getReadingTime } from 'ha/modules/Contentful/utils';
import { CTA_QUERY_PARAM } from 'ha/pages/Blog/constants';

import { PromotedBlogContent } from '../../types';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[200]}`,
    textDecoration: 'none',
    height: '100%',
  },
  label: {
    margin: 0,
    color: theme.palette.grey[700],
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: 1,
  },
  iconContainer: {
    display: 'block',
    height: '100%',
    padding: units.yx(0, 0.75),
  },
  bulbIconContainer: {
    background: theme.palette.secondary.light,
  },
  icon: {
    height: '100%',
    color: theme.palette.secondary.dark,
  },
  bulbIcon: {
    padding: units.rem(0.2),
  },
  arrowIcon: {
    padding: units.rem(0.25),
  },
  contentContainer: {
    flex: 1,
    marginLeft: units.rem(1.25),
    padding: units.yx(1, 0),
  },
  title: {
    margin: 0,
    color: theme.palette.secondary.dark,
    fontWeight: theme.typography.fontWeightBold,
    marginTop: 3,
    lineHeight: 1.25,
  },
}));

type Props = {
  card: PromotedBlogContent;
};

const SearchResultsContentCard: React.FC<Props> = ({
  card: { title, pathname, topicCluster, content },
}) => {
  const { classes, cx } = useStyles();
  const { md } = useMediaQuery();
  const { T } = useIntl();
  const trackEvent = useTrackEvent();

  const readingTime = React.useMemo(() => {
    const time = getReadingTime(false, [content]);

    const timeLabel = T('%s min read', time.timeLength);
    return T('%s in our blog:', timeLabel);
  }, [content, T]);

  React.useEffect(() => {
    trackEvent('Content card loaded', {
      title,
      topicCluster,
    });
  }, [title, topicCluster, trackEvent]);

  const handleClick = React.useCallback(() => {
    trackEvent('Content card clicked', {
      title,
      topicCluster,
    });
  }, [title, topicCluster, trackEvent]);

  const blogPath = `${pathname}?${CTA_QUERY_PARAM}=1`;

  return (
    <Link
      to={blogPath}
      onClick={handleClick}
      className={classes.root}
      target={md ? '_blank' : '_self'}
      key={title}
    >
      <div className={cx(classes.iconContainer, classes.bulbIconContainer)}>
        <LightBulb2Light className={cx(classes.icon, classes.bulbIcon)} />
      </div>
      <div className={classes.contentContainer}>
        <p className={classes.label}>{readingTime}</p>
        <p className={classes.title}>{title}</p>
      </div>
      <div className={classes.iconContainer}>
        <ArrowButtonRightBold className={cx(classes.icon, classes.arrowIcon)} />
      </div>
    </Link>
  );
};

export { SearchResultsContentCard };
